import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ShoppingCart as OrdersIcon,
  Receipt as BillsIcon,
  Assignment as ReturnsIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PurchaseService } from '../services/purchaseService';
import { formatCurrency } from '../utils/formatters';

interface DashboardStats {
  total_orders: number;
  total_orders_amount: number;
  pending_orders: number;
  pending_orders_amount: number;
  total_bills: number;
  total_bills_amount: number;
  unpaid_bills: number;
  unpaid_bills_amount: number;
  total_returns: number;
  total_returns_amount: number;
  top_suppliers: Array<{
    supplier_id: string;
    supplier_name: string;
    total_orders: number;
    total_amount: number;
  }>;
  recent_orders: Array<any>;
  recent_bills: Array<any>;
}

export const PurchaseDashboard: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const data = await PurchaseService.getPurchaseDashboard();
      setStats(data as any);
    } catch (error: any) {
      console.error('Failed to fetch dashboard data:', error);
      if (error.response?.status === 404) {
        enqueueSnackbar('Dashboard endpoint not yet implemented', { variant: 'info' });
        // Set empty stats to show the UI
        setStats({
          total_orders: 0,
          total_orders_amount: 0,
          pending_orders: 0,
          pending_orders_amount: 0,
          total_bills: 0,
          total_bills_amount: 0,
          unpaid_bills: 0,
          unpaid_bills_amount: 0,
          total_returns: 0,
          total_returns_amount: 0,
          top_suppliers: [],
          recent_orders: [],
          recent_bills: [],
        });
      } else {
        enqueueSnackbar('Failed to load dashboard data', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box>
        <Typography>No data available</Typography>
      </Box>
    );
  }

  const StatCard = ({ title, value, subtitle, icon, color }: any) => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div">
              {value}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {subtitle}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}.light`,
              borderRadius: 2,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Purchase Dashboard
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Orders"
            value={stats.total_orders || 0}
            subtitle={formatCurrency(stats.total_orders_amount || 0)}
            icon={<OrdersIcon color="primary" />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Orders"
            value={stats.pending_orders || 0}
            subtitle={formatCurrency(stats.pending_orders_amount || 0)}
            icon={<OrdersIcon color="warning" />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Bills"
            value={stats.total_bills || 0}
            subtitle={formatCurrency(stats.total_bills_amount || 0)}
            icon={<BillsIcon color="success" />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Unpaid Bills"
            value={stats.unpaid_bills || 0}
            subtitle={formatCurrency(stats.unpaid_bills_amount || 0)}
            icon={<MoneyIcon color="error" />}
            color="error"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top Suppliers
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Supplier</TableCell>
                    <TableCell align="right">Orders</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.top_suppliers?.slice(0, 5).map((supplier: any) => (
                    <TableRow key={supplier.supplier_id}>
                      <TableCell>{supplier.supplier_name}</TableCell>
                      <TableCell align="right">{supplier.total_orders}</TableCell>
                      <TableCell align="right">{formatCurrency(supplier.total_amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Purchase Orders
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>PO Number</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.recent_orders?.slice(0, 5).map((order: any) => (
                    <TableRow key={order.id}>
                      <TableCell>{order.po_number}</TableCell>
                      <TableCell>{order.supplier?.name || '-'}</TableCell>
                      <TableCell align="right">{formatCurrency(order.grand_total)}</TableCell>
                      <TableCell>
                        <Chip label={order.status} size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Bills
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Bill Number</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell>Bill Date</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.recent_bills?.slice(0, 10).map((bill: any) => (
                    <TableRow key={bill.id}>
                      <TableCell>{bill.bill_number}</TableCell>
                      <TableCell>{bill.supplier?.name || '-'}</TableCell>
                      <TableCell>{new Date(bill.bill_date).toLocaleDateString()}</TableCell>
                      <TableCell>
                        {bill.due_date ? new Date(bill.due_date).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(bill.total_amount)}</TableCell>
                      <TableCell align="right">{formatCurrency(bill.balance_amount)}</TableCell>
                      <TableCell>
                        <Chip label={bill.status} size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
